import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { useToast } from '@/hooks/use-toast';
import type {
  ExpenseCategory,
  ExpenseCategoryInput,
  ExpenseCategoryUpdate,
} from '@jobos/shared';

interface ExpenseCategoriesResponse {
  categories: ExpenseCategory[];
}

interface ExpenseCategoryResponse {
  category: ExpenseCategory;
}

export const expenseCategoryKeys = {
  all: ['expenseCategories'] as const,
  list: () => [...expenseCategoryKeys.all, 'list'] as const,
};

export function useExpenseCategories() {
  return useQuery({
    queryKey: expenseCategoryKeys.list(),
    queryFn: async () => {
      const response = await apiClient.get<ExpenseCategoriesResponse>('/expense-categories');
      return response.categories;
    },
  });
}

export function useAddExpenseCategory() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (input: ExpenseCategoryInput) => {
      const response = await apiClient.post<ExpenseCategoryResponse>('/expense-categories', input);
      return response.category;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: expenseCategoryKeys.all });
      toast({ title: 'Category added' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message || 'Failed to add category.', variant: 'destructive' });
    },
  });
}

export function useUpdateExpenseCategory() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...updates }: ExpenseCategoryUpdate & { id: string }) => {
      const response = await apiClient.put<ExpenseCategoryResponse>(`/expense-categories/${id}`, updates);
      return response.category;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: expenseCategoryKeys.all });
      toast({ title: 'Category updated' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message || 'Failed to update category.', variant: 'destructive' });
    },
  });
}

export function useDeleteExpenseCategory() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/expense-categories/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: expenseCategoryKeys.all });
      toast({ title: 'Category deleted' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message || 'Failed to delete category.', variant: 'destructive' });
    },
  });
}
